import { createActions } from 'redux-actions';
import { batch } from 'react-redux';

// eslint-disable-next-line import/no-cycle
import {
  divertProgress,
  gotoNextStep,
  markCurrentStepAsCompleted,
  resetCurrentStep,
  idVerificationMaxed,
  RouteToAlert,
} from '../steps/actions';
import { hideMainLoading, showMainLoading, showMainLoadingAsSuccess } from '../main/actions';
import {
  selectMainRegistrationMode,
  selectWorkflowActiveStepLinkByRel,
  selectShadowRegisteredAccountId,
  selectShadowRegisteredCaseId,
  selectIsTrust,
  selectMobileNumber,
  selectTrusteeNames,
  selectUserJourneyAccountType,
} from '../../../selectors';
import registrationApi from '../../../api/registrationApi';
import { getIsoDateString } from '../../../utils/dateutils';
import { ACTION_PREFIX, actionTypes, ID_VERIFICATION_FAILED_ERROR } from './constants';
import { getActionOptions } from '../../../utils/reduxactionsutils';
import {
  GTM_USER_JOURNEY_ERROR_TYPE,
  GTM_USER_JOURNEY_FIELD_NAME,
  NETWORK_CONNECTION_ERROR_MESSAGE,
  NETWORK_ERROR,
} from '../../../constants';
import {
  digitalRegistrationConversion,
  digitalRegistrationStepThreeCompleteEvent,
  getMarketingTagsMapped,
  gtmUserJourneyEvent,
} from '../../../utils/gtmUtils';
import phoneNumberUtils from '../../../utils/phoneNumberUtils';
import startupConfig from '../../../config/startupConfig';
import { GTM_EVENT_TYPE_USER_JOURNEY } from '../../../constants';
import { getSessionId } from '../../../lib/threatmetrix';
import { getAppInsights } from '../../../utils/telemetryUtils';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { AlertRouteConfig } from '../../../config/routeconfig';
import {
  checkSuspectedPEP,
  checkIdVerificationAttemptsMaxed,
  scrollWindowToTop,
} from '../../../utils/regoUtils';

export const {
  updateIdVerificationResult,
  updateErrorMessage,
  updateDirectorList,
  resetIdVerificationResult,
  updateHasErroredFromVerifyIdPrefilled,
  updateShadowRegisteredAccountDetails,
  updateContactRegisterResult,
  updateIsVerifyIdPrefilledError,
} = createActions(
  actionTypes.UPDATE_ID_VERIFICATION_RESULT,
  actionTypes.UPDATE_ERROR_MESSAGE,
  actionTypes.UPDATE_DIRECTOR_LIST,
  actionTypes.RESET_ID_VERIFICATION_RESULT,
  actionTypes.UPDATE_HAS_ERRORED_FROM_VERIFY_ID_PREFILLED,
  actionTypes.UPDATE_SHADOW_REGISTERED_ACCOUNT_DETAILS,
  actionTypes.UPDATE_CONTACT_REGISTER_RESULT,
  actionTypes.UPDATE_IS_VERIFY_ID_PREFILLED_ERROR,
  getActionOptions(ACTION_PREFIX),
);

const updateAccountDetailsIfRequired = (shadowRegisteredAccountId, details, dispatch) => {
  if (!shadowRegisteredAccountId) {
    dispatch(
      updateShadowRegisteredAccountDetails({
        shadowRegisteredAccountId: details.shadowRegisteredAccountId,
      }),
    );
  }
};

export function verifyIdPrefilled(abn) {
  return (dispatch) => {
    batch(() => {
      dispatch(showMainLoading('Verifying details...'));
      dispatch(updateErrorMessage(null));
    });
    return (
      registrationApi
        .verifyIdPrefilled(abn, null)
        .then((response) => {
          const {
            isError,
            links,
            errorMessage,
            verified,
            isBirthDateMatched,
            isNameMatched,
            isTravelDocNoMatched,
            isGenderMatched,
            isExpiryDateMatched,
            isLicenseNoMatched,
            isStateMatched,
            noOfAttempts,
            isCardNumberMatched,
            verificationResultCode,
          } = response.data.output;

          if (!isError && verified) {
            batch(() => {
              dispatch(
                updateIdVerificationResult({
                  isVerified: verified,
                  isBirthDateMatched,
                  isNameMatched,
                  isTravelDocNoMatched,
                  isGenderMatched,
                  isExpiryDateMatched,
                  isLicenseNumberMatched: isLicenseNoMatched,
                  isStateMatched,
                  noOfAttempts,
                  isCardNumberMatched,
                  verificationResultCode,
                }),
              );
            });
          } else {
            batch(() => {
              dispatch(
                updateIdVerificationResult({
                  isVerified: verified || false,
                  isBirthDateMatched: isBirthDateMatched || false,
                  isNameMatched: isNameMatched || false,
                  isTravelDocNoMatched: isTravelDocNoMatched || false,
                  isGenderMatched: isGenderMatched || false,
                  isExpiryDateMatched: isExpiryDateMatched || false,
                  isLicenseNumberMatched: isLicenseNoMatched || false,
                  isStateMatched: isStateMatched || false,
                  noOfAttempts: noOfAttempts || 0,
                  isCardNumberMatched: isCardNumberMatched || false,
                  verificationResultCode: verificationResultCode || '',
                }),
              );
              dispatch(updateHasErroredFromVerifyIdPrefilled(true));
              dispatch(updateIsVerifyIdPrefilledError(true));
              dispatch(updateErrorMessage(errorMessage || ID_VERIFICATION_FAILED_ERROR));
            });
          }
          return {
            shouldInvokeRegisterNext: verified,
            links,
          };
        })
        // eslint-disable-next-line no-unused-vars
        .catch((_reason) => {
          batch(() => {
            dispatch(updateHasErroredFromVerifyIdPrefilled(true));
            dispatch(updateIsVerifyIdPrefilledError(true));
            dispatch(updateErrorMessage('Something went wrong while verifying your details'));
            scrollWindowToTop();
          });
          return null;
        })
        .finally(() => dispatch(hideMainLoading()))
    );
  };
}

export function verifyId({
  nameNotListed,
  firstName,
  lastName,
  email,
  birthDate,
  documentType,
  driversLicenseNumber,
  passportNumber,
  documentExpirationDate,
  consent,
  homeAddress,
  id,
  driversLicenceCardNumber,
  driversLicenceState,
}) {
  return (dispatch, getState) => {
    const state = getState();
    const isTrust = selectIsTrust(state);
    const trusteeNames = selectTrusteeNames(state);
    const userJourneyAccountType = selectUserJourneyAccountType(state);
    const isTrustAsIndividual =
      trusteeNames &&
      trusteeNames.length > 0 &&
      trusteeNames[0].firstName &&
      trusteeNames[0].lastName;
    let stepLink = '';
    if (isTrust && isTrustAsIndividual)
      stepLink = selectWorkflowActiveStepLinkByRel(state, 'VerifyIdTrustee');
    else stepLink = selectWorkflowActiveStepLinkByRel(state, 'VerifyId');

    const appInsights = getAppInsights();

    batch(() => {
      dispatch(resetCurrentStep());
      dispatch(showMainLoading('Verifying details...'));
      dispatch(updateErrorMessage(null));
      dispatch(updateIsVerifyIdPrefilledError(false));
    });

    const selectedAddress = {
      unitNo: homeAddress.subdwelling,
      streetNumber: homeAddress.streetNumber,
      streetName: homeAddress.streetName,
      streetType: homeAddress.streetType,
      suburb: homeAddress.locality,
      state: homeAddress.state,
      postcode: homeAddress.postcode,
    };
    const data = {
      documentType,
      consent,
      firstName,
      lastName,
      licenseNo: driversLicenseNumber,
      travelNo: passportNumber,
      birthDate: getIsoDateString(birthDate),
      nameNotListed,
      address: selectedAddress,
      email,
      gender: '',
      id,
      driversLicenceCardNumber,
      driversLicenceState,
    };

    if (documentExpirationDate) {
      data.expirationDate = getIsoDateString(documentExpirationDate);
    }

    const gtmUserJourneyEventObject = {
      eventType: GTM_EVENT_TYPE_USER_JOURNEY.REGO_PERSONAL_DETAILS,
      accountType: userJourneyAccountType,
      documentType: documentType,
    };
    gtmUserJourneyEvent(gtmUserJourneyEventObject);

    return (
      registrationApi
        .verifyId(stepLink.href, data)
        .then((response) => {
          const {
            isError,
            links,
            errorMessage,
            verified,
            isBirthDateMatched,
            isNameMatched,
            isTravelDocNoMatched,
            isGenderMatched,
            isExpiryDateMatched,
            isLicenseNoMatched,
            isStateMatched,
            noOfAttempts,
            isCardNumberMatched,
            verificationResultCode,
          } = response.data.output;

          if (!isError && verified) {
            batch(() => {
              dispatch(
                updateIdVerificationResult({
                  isVerified: verified,
                  isBirthDateMatched,
                  isNameMatched,
                  isTravelDocNoMatched,
                  isGenderMatched,
                  isExpiryDateMatched,
                  isLicenseNumberMatched: isLicenseNoMatched,
                  isStateMatched,
                  noOfAttempts,
                  isCardNumberMatched,
                  verificationResultCode,
                }),
              );
            });
          } else if (links && links.length && links[0].rel === 'TooManyAttempts') {
            dispatch(
              divertProgress({
                isBlocked: true,
                isSuccess: false,
                message: errorMessage,
              }),
            );
            dispatch(RouteToAlert(AlertRouteConfig.TooManyAttempts));
          } else {
            batch(() => {
              const gtmUserJourneyEventObject = {
                eventType: GTM_EVENT_TYPE_USER_JOURNEY.REGO_PERSONAL_DETAILS_FAILED,
                accountType: userJourneyAccountType,
                documentType: documentType,
                isError: true,
                errorType: GTM_USER_JOURNEY_ERROR_TYPE.IDENTITY_VERIFICATION_FAILED,
                fieldName: errorMessage || GTM_USER_JOURNEY_FIELD_NAME.IDENTITY_VERIFICATION_ERROR,
              };
              gtmUserJourneyEvent(gtmUserJourneyEventObject);
              dispatch(
                updateIdVerificationResult({
                  isVerified: verified || false,
                  isBirthDateMatched: isBirthDateMatched || false,
                  isNameMatched: isNameMatched || false,
                  isTravelDocNoMatched: isTravelDocNoMatched || false,
                  isGenderMatched: isGenderMatched || false,
                  isExpiryDateMatched: isExpiryDateMatched || false,
                  isLicenseNumberMatched: isLicenseNoMatched || false,
                  isStateMatched: isStateMatched || false,
                  noOfAttempts: noOfAttempts || 1,
                  isCardNumberMatched: isCardNumberMatched || false,
                  verificationResultCode: verificationResultCode || '',
                }),
              );
              dispatch(updateErrorMessage(errorMessage || ID_VERIFICATION_FAILED_ERROR));
            });
          }
          return {
            shouldInvokeRegisterNext: verified,
            links,
          };
        })
        // eslint-disable-next-line no-unused-vars
        .catch((_reason) => {
          const gtmUserJourneyEventObject = {
            eventType: GTM_EVENT_TYPE_USER_JOURNEY.REGO_PERSONAL_DETAILS,
            accountType: userJourneyAccountType,
            documentType: documentType,
            isError: true,
            errorType: GTM_USER_JOURNEY_ERROR_TYPE.SOMETHING_WENT_WRONG_DURING_REGISTRATION,
          };
          gtmUserJourneyEvent(gtmUserJourneyEventObject);
          dispatch(updateErrorMessage('Something went wrong while verifying your details'));
          scrollWindowToTop();
          return null;
        })
        .finally(() => dispatch(hideMainLoading()))
    );
  };
}

export function registerAccount(
  updateErrorMessageCallback,
  stepLinkHref,
  callback,
  errorCallback,
  verificationDetails,
) {
  return async (dispatch, getState) => {
    digitalRegistrationStepThreeCompleteEvent();
    dispatch(showMainLoading('Please wait while we register your details...'));

    const appInsights = getAppInsights();
    const isShadowRegisterOnStep2Enabled =
      startupConfig.get().featureConfiguration.isShadowRegisterOnStep2Enabled;
    const isUploadIdFlowEnabled = startupConfig.get().featureConfiguration.isUploadIdFlowEnabled;
    const state = getState();
    const shadowRegisteredAccountId = selectShadowRegisteredAccountId(state);
    const shadowRegisteredCaseId = selectShadowRegisteredCaseId(state);
    const registrationMode = selectMainRegistrationMode(state);
    const userJourneyAccountType = selectUserJourneyAccountType(state);
    const formattedMobileNumber = phoneNumberUtils.formatMobilePhoneNumber(
      selectMobileNumber(state),
    );
    let isDisplayUploadID = false;
    const isAdminRegistration = registrationMode === 'rm';
    const marketingTagsMapped = await getMarketingTagsMapped();

    let verificationRequest = null;
    if (verificationDetails && !verificationDetails.isPrefilledIdVerified) {
      const gtmUserJourneyEventObject = {
        eventType: GTM_EVENT_TYPE_USER_JOURNEY.REGO_PERSONAL_DETAILS,
        accountType: userJourneyAccountType,
        documentType: verificationDetails.documentType,
      };

      gtmUserJourneyEvent(gtmUserJourneyEventObject);

      const {
        nameNotListed,
        firstName,
        lastName,
        email,
        birthDate,
        documentType,
        driversLicenseNumber,
        passportNumber,
        documentExpirationDate,
        consent,
        homeAddress,
        id,
        driversLicenceCardNumber,
        driversLicenceState,
      } = verificationDetails;
      const selectedAddress = {
        unitNo: homeAddress.subdwelling,
        streetNumber: homeAddress.streetNumber,
        streetName: homeAddress.streetName,
        streetType: homeAddress.streetType,
        suburb: homeAddress.locality,
        state: homeAddress.state,
        postcode: homeAddress.postcode,
      };
      verificationRequest = {
        documentType,
        consent,
        firstName,
        lastName,
        licenseNo: driversLicenseNumber,
        travelNo: passportNumber,
        birthDate: getIsoDateString(birthDate),
        nameNotListed,
        address: selectedAddress,
        email,
        gender: '',
        id,
        driversLicenceCardNumber,
        gcAccountId: shadowRegisteredAccountId,
        expirationDate: getIsoDateString(documentExpirationDate),
        driversLicenceState,
      };
    }

    const skipIdVerificationForPrefilledId =
      verificationDetails && verificationDetails.isPrefilledIdVerified;

    return (
      registrationApi
        .register(stepLinkHref, {
          ...marketingTagsMapped,
          isShadowRegistered: isShadowRegisterOnStep2Enabled ? true : false,
          shadowRegisteredAccountId: shadowRegisteredAccountId,
          shadowRegisteredCaseId: shadowRegisteredCaseId,
          isAdminRegistration: isAdminRegistration,
          tmxSessionId: getSessionId(),
          isPrefilledIdVerified: skipIdVerificationForPrefilledId,
          verificationRequest,
        })
        .then(async (response) => {
          var contactRegisterDetails = response && response.data && response.data.output;
          updateAccountDetailsIfRequired(
            shadowRegisteredAccountId,
            contactRegisterDetails,
            dispatch,
          );
          var verificationResult = contactRegisterDetails.verificationResult;
          var isIdVerificationAttemptsMaxed = checkIdVerificationAttemptsMaxed(verificationResult);

          var trimmedPepStatus = contactRegisterDetails.pepStatus;
          if (trimmedPepStatus) {
            trimmedPepStatus = trimmedPepStatus.split(' ').join('');
          }
          var isSuspectedPEP = checkSuspectedPEP(trimmedPepStatus);

          if (isUploadIdFlowEnabled && verificationResult && verificationDetails) {
            checkVerificationResult(
              verificationResult,
              verificationDetails.documentType,
              dispatch,
              getState,
              isIdVerificationAttemptsMaxed,
              shadowRegisteredAccountId,
            );
          }

          if (isUploadIdFlowEnabled && contactRegisterDetails) {
            isDisplayUploadID =
              isIdVerificationAttemptsMaxed ||
              contactRegisterDetails.isPotentialAMLIdentified ||
              isSuspectedPEP;

            dispatch(
              updateContactRegisterResult({
                pepStatus: trimmedPepStatus,
                isPotentialAMLIdentified: contactRegisterDetails.isPotentialAMLIdentified,
                sfContactId: contactRegisterDetails.sfContactId,
              }),
            );
          }

          if (callback) {
            callback();
          }
          const { output } = response.data;
          const { links, errorMessage, isRegistered } = output;

          digitalRegistrationConversion(output?.accountId);
          if (isRegistered) {
            dispatch(updateErrorMessageCallback(''));
            if (!isDisplayUploadID) {
              const gtmUserJourneyEventObject = {
                eventType: GTM_EVENT_TYPE_USER_JOURNEY.REGO_REGISTRATION_SUCCESS,
                accountType: userJourneyAccountType,
                mobileNumber: formattedMobileNumber,
                documentType: verificationRequest?.documentType,
                userId: contactRegisterDetails.sfContactId,
              };
              gtmUserJourneyEvent(gtmUserJourneyEventObject);
            }

            batch(() => {
              if (links && links.length) {
                dispatch(markCurrentStepAsCompleted({ nextStepLinks: links }));
                const link = links[0];
                if (isAdminRegistration) {
                  if (link.rel === '') {
                    batch(() => {
                      dispatch(hideMainLoading());
                      dispatch(
                        divertProgress({
                          isBlocked: false,
                          isSuccess: true,
                          message: 'Thank You for entering the details.',
                        }),
                      );
                      dispatch(RouteToAlert(AlertRouteConfig.AdminSuccess));
                    });
                    return;
                  }
                  batch(() => {
                    dispatch(hideMainLoading());
                    dispatch(gotoNextStep());
                  });
                  return;
                }

                if (link.rel === 'Completed' && link.href) {
                  dispatch(
                    showMainLoadingAsSuccess('Success! Please wait while we redirect you...'),
                  );
                  setTimeout(() => {
                    // eslint-disable-next-line no-undef
                    window.location = link.href;
                  }, 3000);
                  return;
                }

                if (isDisplayUploadID) {
                  appInsights.trackTrace({
                    message: `SuspectedPEP/AML/Max KYC attempts failed - Initiated ID upload - gcAccountId: ${shadowRegisteredAccountId} - firstName:${verificationDetails.firstName} - lastName:${verificationDetails.lastName}`,
                    severityLevel: SeverityLevel.Information,
                  });
                  if (callback) {
                    callback();
                  }

                  dispatch(hideMainLoading());
                  return;
                }

                batch(() => {
                  dispatch(hideMainLoading());
                  dispatch(
                    divertProgress({
                      isBlocked: false,
                      isSuccess: true,
                      message:
                        'Thank You for entering the details. We will call you shortly to complete your registration.',
                    }),
                  );
                  dispatch(RouteToAlert(AlertRouteConfig.VerifyIdSuccess));
                });
                return;
              }

              // falback
              dispatch(hideMainLoading());
              dispatch(
                divertProgress({
                  isBlocked: false,
                  isSuccess: true,
                  message: 'Success! You have been registered successfully.',
                }),
              );
            });
          } else {
            if (errorCallback) {
              errorCallback();
            }
            batch(() => {
              dispatch(hideMainLoading());
              dispatch(updateErrorMessageCallback(errorMessage));
            });
          }
        })
        // eslint-disable-next-line no-unused-vars
        .catch((_reason) => {
          appInsights.trackTrace({
            message: `Registration failed, reason:  ${_reason} - gcAccountId: ${shadowRegisteredAccountId} - firstName:${
              verificationDetails && verificationDetails.firstName
            } - lastName:${verificationDetails && verificationDetails.lastName}`,
            severityLevel: SeverityLevel.Error,
          });
          const gtmUserJourneyEventObject = {
            eventType: GTM_EVENT_TYPE_USER_JOURNEY.REGO_PERSONAL_DETAILS,
            accountType: userJourneyAccountType,
            isError: true,
            errorType: GTM_USER_JOURNEY_ERROR_TYPE.SOMETHING_WENT_WRONG_DURING_REGISTRATION,
          };
          gtmUserJourneyEvent(gtmUserJourneyEventObject);
          if (callback) {
            callback();
          }
          if (errorCallback) {
            errorCallback();
          }
          const networkError =
            _reason.message &&
            _reason.message === NETWORK_ERROR &&
            NETWORK_CONNECTION_ERROR_MESSAGE;
          batch(() => {
            dispatch(hideMainLoading());
            dispatch(
              updateErrorMessageCallback(
                networkError || 'Something went wrong during registration',
              ),
            );
          });
          scrollWindowToTop();
        })
    );
  };
}

function checkVerificationResult(
  verificationResult,
  documentType,
  dispatch,
  getState,
  isIdVerificationAttemptsMaxed,
  shadowRegisteredAccountId,
) {
  const {
    isError,
    links,
    errorMessage,
    verified,
    isBirthDateMatched,
    isNameMatched,
    isTravelDocNoMatched,
    isGenderMatched,
    isExpiryDateMatched,
    isLicenseNoMatched,
    isStateMatched,
    noOfAttempts,
    isCardNumberMatched,
    verificationResultCode,
  } = verificationResult;
  const appInsights = getAppInsights();
  const state = getState();
  const userJourneyAccountType = selectUserJourneyAccountType(state);
  if (!isError && verified) {
    batch(() => {
      dispatch(
        updateIdVerificationResult({
          isVerified: verified,
          isBirthDateMatched,
          isNameMatched,
          isTravelDocNoMatched,
          isGenderMatched,
          isExpiryDateMatched,
          isLicenseNumberMatched: isLicenseNoMatched,
          isStateMatched,
          noOfAttempts,
          isCardNumberMatched,
          verificationResultCode,
        }),
      );
    });
  } else if (isIdVerificationAttemptsMaxed) {
    dispatch(
      idVerificationMaxed({
        isIdVerificationAttemptsMaxed: true,
      }),
    );
    appInsights.trackTrace({
      message: `TooManyAttempts - Initiated ID upload - gcAccountId: ${shadowRegisteredAccountId}`,
      severityLevel: SeverityLevel.Information,
    });
  } else {
    batch(() => {
      const gtmUserJourneyEventObject = {
        eventType: GTM_EVENT_TYPE_USER_JOURNEY.REGO_PERSONAL_DETAILS_FAILED,
        accountType: userJourneyAccountType,
        documentType: documentType,
        isError: true,
        errorType: GTM_USER_JOURNEY_ERROR_TYPE.IDENTITY_VERIFICATION_FAILED,
        fieldName: errorMessage || GTM_USER_JOURNEY_FIELD_NAME.IDENTITY_VERIFICATION_ERROR,
      };
      gtmUserJourneyEvent(gtmUserJourneyEventObject);
      dispatch(
        updateIdVerificationResult({
          isVerified: verified || false,
          isBirthDateMatched: isBirthDateMatched || false,
          isNameMatched: isNameMatched || false,
          isTravelDocNoMatched: isTravelDocNoMatched || false,
          isGenderMatched: isGenderMatched || false,
          isExpiryDateMatched: isExpiryDateMatched || false,
          isLicenseNumberMatched: isLicenseNoMatched || false,
          isStateMatched: isStateMatched || false,
          noOfAttempts: noOfAttempts || 1,
          isCardNumberMatched: isCardNumberMatched || false,
          verificationResultCode: verificationResultCode || '',
        }),
      );
      dispatch(updateErrorMessage(errorMessage || ID_VERIFICATION_FAILED_ERROR));
    });
  }
}

export function shadowRegisterAccount(updateErrorMessageCallback, abn) {
  return async (dispatch, getState) => {
    const appInsights = getAppInsights();

    if (!abn || !abn.id) {
      appInsights.trackTrace({
        message: 'Registration failed, ABN details missing',
        severityLevel: SeverityLevel.Error,
      });
      updateErrorMessageCallback('Something went wrong during registration');
      return;
    }
    dispatch(showMainLoading('Registering account...'));

    var state = getState();
    const formattedMobileNumber = phoneNumberUtils.formatMobilePhoneNumber(
      selectMobileNumber(state),
    );
    const registrationMode = selectMainRegistrationMode(state);
    const marketingTagsMapped = await getMarketingTagsMapped();

    var request = {
      ...marketingTagsMapped,
      mobile: formattedMobileNumber,
      abn: abn.id,
      isAdminRegistration: registrationMode === 'rm',
    };

    return registrationApi
      .createAccountByABN(abn.id, request)
      .then((response) => {
        dispatch(updateShadowRegisteredAccountDetails(response.data));
        return response.data;
      })
      .catch((_reason) => {
        appInsights.trackTrace({
          message: `Registration failed, reason: ${_reason} abn: ${abn.id} mobile: ${formattedMobileNumber}`,
          severityLevel: SeverityLevel.Error,
        });
        const networkError =
          _reason.message && _reason.message === NETWORK_ERROR && NETWORK_CONNECTION_ERROR_MESSAGE;
        batch(() => {
          dispatch(hideMainLoading());
          dispatch(
            updateErrorMessageCallback(networkError || 'Something went wrong during registration'),
          );
        });
      })
      .finally(() => dispatch(hideMainLoading()));
  };
}

export function handleIdUploadSuccess(
  isIdVerificationAttemptsMaxed,
  nameNotListed,
  isPrefilledIdVerified,
  abn,
  idType,
  userJourneyAccountType,
  isIdUploadFail,
  updateErrorMessageCallback,
) {
  return async (dispatch, getState) => {
    if (!isIdUploadFail) {
      const gtmUserJourneyEventObject = {
        eventType: GTM_EVENT_TYPE_USER_JOURNEY.REGO_ID_UPLOAD_SUCCESS,
        accountType: userJourneyAccountType,
        documentType: idType,
      };
      gtmUserJourneyEvent(gtmUserJourneyEventObject);
    }

    const state = getState();
    const shadowRegisteredAccountId = selectShadowRegisteredAccountId(state);
    await updateRegistrationAttemptCase(
      state,
      shadowRegisteredAccountId,
      isPrefilledIdVerified,
      abn,
      updateErrorMessageCallback,
      dispatch,
    );

    if (isIdVerificationAttemptsMaxed || nameNotListed) {
      //thank you page shown if - kyc attempt maxed or name not here flow
      dispatch(
        divertProgress({
          isBlocked: false,
          isSuccess: true,
          message: 'ID upload is successful.',
        }),
      );
      dispatch(RouteToAlert(AlertRouteConfig.UploadIdSucess));
    }
  };
}

async function updateRegistrationAttemptCase(
  state,
  shadowRegisteredAccountId,
  isPrefilledIdVerified,
  abn,
  updateErrorMessageCallback,
  dispatch,
) {
  const formattedMobileNumber = phoneNumberUtils.formatMobilePhoneNumber(selectMobileNumber(state));
  const marketingTagsMapped = await getMarketingTagsMapped();

  var request = {
    ...marketingTagsMapped,
    getCapitalAccountId: shadowRegisteredAccountId,
    mobileNumber: formattedMobileNumber,
    idUploaded: true,
    portal: state.mainPage.portal,
    isPrefilledIdVerified: isPrefilledIdVerified,
    abn: abn,
  };

  try {
    await registrationApi.updateRegistrationAttemptCase(request);
  } catch (error) {
    const networkError =
      error.message && error.message === NETWORK_ERROR && NETWORK_CONNECTION_ERROR_MESSAGE;
    dispatch(updateErrorMessageCallback(networkError || 'Something went wrong'));
  }
}

export function updateCaseContact(data, updateErrorMessageCallback) {
  return async (dispatch, getState) => {
    const state = getState();
    const shadowRegisteredAccountId = selectShadowRegisteredAccountId(state);
    const formattedMobileNumber = phoneNumberUtils.formatMobilePhoneNumber(
      selectMobileNumber(state),
    );
    const marketingTagsMapped = await getMarketingTagsMapped();

    var request = {
      ...marketingTagsMapped,
      getCapitalAccountId: shadowRegisteredAccountId,
      mobileNumber: formattedMobileNumber,
      portal: state.mainPage.portal,
      isUpdateSsoContact: true,
      firstName: data.firstName,
      lastName: data.lastName,
      dateOfBirth: data.dateOfBirth,
    };

    try {
      await registrationApi.updateRegistrationAttemptCase(request);
    } catch (error) {
      const networkError =
        error.message && error.message === NETWORK_ERROR && NETWORK_CONNECTION_ERROR_MESSAGE;
      dispatch(updateErrorMessageCallback(networkError || 'Something went wrong'));
    }
  };
}

export function ssoExistingContact(abn, ssoExistingContactRequest, updateErrorMessageCallback) {
  return async (dispatch, getState) => {
    dispatch(showMainLoading('Success! Please wait while we redirect you...'));
    const appInsights = getAppInsights();
    const state = getState();
    const formattedMobileNumber = phoneNumberUtils.formatMobilePhoneNumber(
      selectMobileNumber(state),
    );
    const marketingTagsMapped = await getMarketingTagsMapped();
    return registrationApi
      .ssoExistingContact(abn, {
        gcAccountId: ssoExistingContactRequest.shadowRegisteredAccountId,
        mobileNumber: formattedMobileNumber,
        dateOfBirth: ssoExistingContactRequest.dateOfBirth,
        firstName: ssoExistingContactRequest.firstName,
        lastName: ssoExistingContactRequest.lastName,
        isRegistrantEligibleToRedirectToGco:
          ssoExistingContactRequest.isRegistrantEligibleToRedirectToGco,
        isRegistrantEligibleToRedirectToSP:
          ssoExistingContactRequest.isRegistrantEligibleToRedirectToSP,
        referrerRequest: marketingTagsMapped,
      })
      .then(async (response) => {
        const { links } = response.data;
        if (links && links.length > 0 && links[0].href) {
          appInsights.trackTrace({
            message: `SSO success - ssoExistingContact - gcAccountId: ${ssoExistingContactRequest.shadowRegisteredAccountId} mobileNumber: ${formattedMobileNumber} firstName: ${ssoExistingContactRequest.firstName} lastName: ${ssoExistingContactRequest.lastName}`,
            severityLevel: SeverityLevel.Information,
          });
          setTimeout(() => {
            // eslint-disable-next-line no-undef
            window.location = links[0].href;
          }, 3000);
          return { isOnNameClickSSOSuccess: true };
        }
        dispatch(hideMainLoading());
        return { isOnNameClickSSOSuccess: false };
      })
      .catch((_reason) => {
        appInsights.trackTrace({
          message: `SSO Error - ssoExistingContact - gcAccountId: ${ssoExistingContactRequest.shadowRegisteredAccountId} mobileNumber: ${formattedMobileNumber} firstName: ${ssoExistingContactRequest.firstName} lastName: ${ssoExistingContactRequest.lastName}`,
          severityLevel: SeverityLevel.Error,
        });
        const networkError =
          _reason.message && _reason.message === NETWORK_ERROR && NETWORK_CONNECTION_ERROR_MESSAGE;
        batch(() => {
          dispatch(hideMainLoading());
          dispatch(updateErrorMessageCallback(networkError || 'Something went wrong'));
        });
        return { isOnNameClickSSOSuccess: false };
      });
  };
}

export function ssoContactPrefilledId(abn, updateErrorMessageCallback) {
  return async (dispatch, getState) => {
    dispatch(showMainLoading('Success! Please wait while we redirect you...'));
    const appInsights = getAppInsights();
    const state = getState();
    const formattedMobileNumber = phoneNumberUtils.formatMobilePhoneNumber(
      selectMobileNumber(state),
    );
    const marketingTagsMapped = await getMarketingTagsMapped();
    return registrationApi
      .ssoContactPrefilledId(abn, {
        mobileNumber: formattedMobileNumber,
        referrerRequest: marketingTagsMapped,
      })
      .then(async (response) => {
        const { links } = response.data;
        if (links && links.length > 0 && links[0].href) {
          appInsights.trackTrace({
            message: `SSO success - ssoContactPrefilledId - abn: ${abn} mobileNumber: ${formattedMobileNumber}`,
            severityLevel: SeverityLevel.Information,
          });
          setTimeout(() => {
            // eslint-disable-next-line no-undef
            window.location = links[0].href;
          }, 3000);
          return;
        }
        dispatch(hideMainLoading());
        return;
      })
      .catch((_reason) => {
        appInsights.trackTrace({
          message: `SSO Error - ssoContactPrefilledId - abn: ${abn} mobileNumber: ${formattedMobileNumber}`,
          severityLevel: SeverityLevel.Error,
        });
        const networkError =
          _reason.message && _reason.message === NETWORK_ERROR && NETWORK_CONNECTION_ERROR_MESSAGE;
        batch(() => {
          dispatch(hideMainLoading());
          dispatch(updateErrorMessageCallback(networkError || 'Something went wrong'));
        });
        return;
      });
  };
}

export function verifySso(verifySsoRequest, updateErrorMessageCallback) {
  return async (dispatch, getState) => {
    dispatch(showMainLoading('Please wait while we verify your details...'));
    const appInsights = getAppInsights();
    const state = getState();
    const formattedMobileNumber = phoneNumberUtils.formatMobilePhoneNumber(
      selectMobileNumber(state),
    );

    return registrationApi
      .verifySso({
        gcAccountId: verifySsoRequest.shadowRegisteredAccountId,
        mobileNumber: formattedMobileNumber,
        dateOfBirth: verifySsoRequest.dateOfBirth,
        firstName: verifySsoRequest.firstName,
        lastName: verifySsoRequest.lastName,
      })
      .then(async (response) => {
        dispatch(hideMainLoading());
        if (response && response.data) {
          return true;
        }
        return false;
      })
      .catch((_reason) => {
        appInsights.trackTrace({
          message: `SSO verification Error - on name click - gcAccountId: ${verifySsoRequest.shadowRegisteredAccountId} mobileNumber: ${formattedMobileNumber} firstName: ${verifySsoRequest.firstName} lastName: ${verifySsoRequest.lastName}`,
          severityLevel: SeverityLevel.Error,
        });
        const networkError =
          _reason.message && _reason.message === NETWORK_ERROR && NETWORK_CONNECTION_ERROR_MESSAGE;
        batch(() => {
          dispatch(hideMainLoading());
          dispatch(updateErrorMessageCallback(networkError || 'Something went wrong'));
        });
        return false;
      });
  };
}
